import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const FAQ_CATEGORIES: FAQCategory[] = [
  {
    title: 'Made to Measure',
    items: [
      {
        question: 'How does the made-to-measure process work?',
        answer: 'Select any piece marked "Made to Measure," and you\'ll be guided through a measurement form before it\'s added to your bag. If you\'ve ordered from us before, your last measurements will be pre-filled so you can simply confirm they\'re still accurate rather than starting over.',
      },
      {
        question: 'What if I don\'t know how to take my measurements?',
        answer: 'Every measurement form includes a short how-to video showing exactly how to measure each area. Take your time and measure over close-fitting clothing for the most accurate result.',
      },
      {
        question: 'Can I update my saved measurements?',
        answer: 'Yes — anytime you fill out a measurement form again, your latest entries automatically become your new saved measurements for next time.',
      },
    ],
  },
  {
    title: 'Shipping',
    items: [
      {
        question: 'How is shipping calculated for standard items?',
        answer: 'Standard items (accessories, ready-to-wear pieces made locally) ship via USPS/UPS through our shipping partner. You\'ll choose a shipping method at checkout with the cost and estimated delivery window shown upfront.',
      },
      {
        question: 'How does shipping work for Nigeria-made pieces (agbada, kaftans, etc.)?',
        answer: 'These ship differently since they\'re produced in Nigeria. At checkout, you\'ll choose one of three options: Pick Up In Store (free), Local Delivery within Nigeria, or International Delivery. For local and international delivery, you won\'t be charged shipping at checkout — our team will follow up with you directly on WhatsApp with the exact cost once your order is ready, based on your location or the item\'s weight.',
      },
      {
        question: 'Can I order Nigeria-made pieces and standard items together?',
        answer: 'Not in the same order, since they ship completely differently. If your bag has both, you\'ll be asked to complete them as two separate orders.',
      },
      {
        question: 'How do I track my order?',
        answer: 'Once your order ships, you\'ll receive an email with a tracking number and a direct link to track it with the carrier. You can also view tracking anytime from your Account page under "My Orders."',
      },
    ],
  },
  {
    title: 'Payments',
    items: [
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept card payments (Visa, Mastercard, and more) for customers everywhere, and Paystack (card, bank transfer, USSD) specifically for customers paying in Nigerian Naira.',
      },
      {
        question: 'Is it safe to enter my card details?',
        answer: 'Yes — all payments are processed securely through Stripe or Paystack. We never see or store your full card details on our servers.',
      },
      {
        question: 'My currency shows differently than expected — why?',
        answer: 'We automatically detect your currency based on your location, but you can change it anytime using the currency selector in the navigation bar. Prices update instantly, and you\'ll see a note on available payment methods when you switch.',
      },
    ],
  },
  {
    title: 'Orders & Account',
    items: [
      {
        question: 'I started an order but didn\'t finish paying — what happens now?',
        answer: 'Your order is saved as pending in your account under "My Orders." You can return anytime to complete payment. We\'ll also send a friendly reminder email if it\'s still unpaid after a couple of days.',
      },
      {
        question: 'Can I manage multiple delivery addresses?',
        answer: 'Yes — under Account → Addresses, you can add, edit, delete, or set a default address at any time.',
      },
      {
        question: 'How do I contact support?',
        answer: 'Use the chat button in the bottom-right corner of any page once you\'re logged in — our team typically replies within minutes. If it\'s been a few minutes with no reply, you\'ll see an option to continue the conversation on WhatsApp instead.',
      },
    ],
  },
];

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggle = (key: string) => {
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="animate-fade-in">
      <section className="bg-light py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs tracking-widest uppercase text-nude mb-3">
            We're Here to Help
          </p>
          <h1 className="font-serif text-5xl text-primary">Frequently Asked Questions</h1>
        </motion.div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-24 space-y-14">
        {FAQ_CATEGORIES.map((category) => (
          <div key={category.title}>
            <h2 className="font-serif text-2xl text-primary mb-6">{category.title}</h2>
            <div className="space-y-3">
              {category.items.map((item, idx) => {
                const key = `${category.title}-${idx}`;
                const isOpen = openIndex === key;
                return (
                  <div key={key} className="border border-light">
                    <button
                      onClick={() => toggle(key)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <span className="text-sm font-medium text-primary pr-4">{item.question}</span>
                      <ChevronDown
                        size={16}
                        className={`text-nude flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4">
                        <p className="text-xs text-secondary leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="text-center pt-8 border-t border-light">
          <p className="text-xs text-nude tracking-wide mb-4">
            Still have a question?
          </p>
          <p className="text-sm text-secondary">
            Use the chat button in the corner of your screen, or visit our{' '}
            <a href="/contact" className="text-primary underline hover:text-secondary transition-colors">
              Contact page
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;